/**
 * Remove, on the People tab: who it is offered for, and what its confirm says.
 *
 * It calls the backend's removeUserFromGroup (DELETE
 * group/{grpId}/removeParticipant/{userId}). That takes the person out of every
 * role they hold in the program (members, admins, mentors, mentees) and ends
 * their matches in it, so a learner, a mentor and an admin of the program can
 * each be removed. Their answers are kept: added back, they carry on where they
 * left off. It is never offered:
 *   • on the viewer's own row;
 *   • on the program's owner (its creator when it has no owner, as on the card);
 *   • on an org admin, who reaches every program through the organization;
 *   • on someone invited who hasn't signed up yet: they aren't a member, so
 *     there is nobody to remove (their invitation stands).
 *
 * No React here, so it can be tested on its own (peopleRemoval.test.ts).
 */

export interface RemovablePerson {
  id: string;
  /** Invited and not signed up yet. */
  invited: boolean;
}

export interface RemovalContext {
  /** The viewer may change the program (program.canEdit). */
  canEdit: boolean;
  viewerId: string;
  ownerId: string | null;
  /** The org admins among the program's people; null until known, and Remove waits for it. */
  orgAdminIds: ReadonlySet<string> | null;
}

export const canRemove = (person: RemovablePerson, ctx: RemovalContext): boolean =>
  ctx.canEdit &&
  ctx.orgAdminIds !== null &&
  !person.invited &&
  !!person.id &&
  person.id !== ctx.viewerId &&
  person.id !== ctx.ownerId &&
  !ctx.orgAdminIds.has(person.id);

/** A learner of the program (the API's role code is "mentee"). */
export const isLearner = (roles: string[]): boolean => roles.some((r) => String(r).trim().toLowerCase() === 'mentee');

/** The confirm's title. */
export const removalTitle = (name: string, program: string): string => `Remove ${name} from ${program}?`;

/** The toast once they are removed. */
export const removedToast = (name: string, program: string): string => `${name} is removed from ${program}.`;

/**
 * The confirm's body. `fromAudience`: the person came in through the audience
 * (Added by: Audience). `holdsProgramRole`: a learner who holds one of the
 * program's roles (a program for roles).
 */
export function removalMessage(p: { fromAudience: boolean; holdsProgramRole: boolean }): string {
  const lines = ['Their progress is kept. If you add them back later, they carry on where they left off.'];
  if (p.fromAudience) lines.push("They won't be added back automatically, even if they still match the audience.");
  if (p.holdsProgramRole) {
    lines.push("They hold one of this program's roles, so they keep the role; only this program is removed.");
  }
  return lines.join(' ');
}

/** "1 person", "3 people". */
export const peopleCount = (n: number): string => (n === 1 ? '1 person' : `${n} people`);

/** The confirm's title when several are removed at once. */
export const bulkRemovalTitle = (n: number, program: string): string => `Remove ${peopleCount(n)} from ${program}?`;

/** The confirm's body when several are removed at once: what holds for each of them. */
export const bulkRemovalMessage = (): string =>
  "Their progress is kept. If you add them back later, they carry on where they left off. They won't be added back automatically, even if they still match the audience.";

/** The toast once they are removed. */
export const bulkRemovedToast = (n: number, program: string): string => `${peopleCount(n)} removed from ${program}.`;

/**
 * Removes each person in turn, `concurrency` at a time, and says who left and
 * who didn't. One failure doesn't stop the others.
 */
export async function removeEach(
  ids: string[],
  removeOne: (id: string) => Promise<unknown>,
  concurrency = 3
): Promise<{ removed: string[]; failed: string[] }> {
  const removed: string[] = [];
  const failed: string[] = [];
  let next = 0;
  const worker = async () => {
    while (next < ids.length) {
      const id = ids[next++];
      try {
        await removeOne(id);
        removed.push(id);
      } catch {
        failed.push(id);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, ids.length)) }, worker));
  // In the order they were asked for, whatever order they finished in.
  const order = (a: string, b: string) => ids.indexOf(a) - ids.indexOf(b);
  return { removed: removed.sort(order), failed: failed.sort(order) };
}
